import { ShaderMaterial } from "three";
import { ControllerHand, InputDeviceType } from "flavr-common";
import { Matrix4 } from "flavr-math";
import { LEFT_HAND_MESH_INFO, RIGHT_HAND_MESH_INFO, getControllerMeshInfo } from "@modules/inputs";
import { useExternalMessages, modelPool, computeLocalMatrixFromWorldMatrix } from "@modules/model";
import { MeshInstance } from "../mesh/MeshInstance";
import { Renderable } from "../renderable/Renderable";
import { applyUserWireframeToMeshes } from "../shader/BarycentricWireframe";
const tempTransform = new Matrix4();
/**
 * Responsible for the visible representation of a VR-controller,
 * usually a hand-mesh and a controller-mesh.
 */
export class ControllerRenderable extends Renderable {
    constructor(owner) {
        super(owner);
        this.type = ControllerRenderable.typestatic;
        this.attachedRenderables = new Map();
        /**
         * A callback.
         * Is called after this.handMeshRenderable has actually loaded new Geometries.
         * Does call sanity-check, adds barycentric coordinates to the mesh and applies the hand-material.
         */
        this.onHandMeshLoaded = (group, instance) => {
            this.sanityCheckHand();
            applyUserWireframeToMeshes(group, true);
            this.applyHandColor();
            this.owner.addObjectToPersistentRoot(group);
            instance.updateTransform(this.owner.matrixWorld);
        };
        this.onControllerMeshLoaded = (group, instance) => {
            this.sanityCheckController();
            this.owner.addObjectToPersistentRoot(group);
            instance.updateTransform(this.owner.matrixWorld);
        };
        // Add a callback to after the mesh is completely loaded,
        // so we can do stuff to the mesh that our fancy shader needs.
        this.handMesh = new MeshInstance(owner.renderer, this.onHandMeshLoaded);
    }
    addChildModel(model) {
        this.attachedRenderables.set(model.modelUID, {
            model,
            offsetMatrix: new Matrix4().compose(model.worldPosition, model.worldOrientation, model.worldScale)
        });
    }
    removeChildModel(model) {
        this.attachedRenderables.delete(model.modelUID);
    }
    destroy() {
        this.handMesh.destroy();
        if (this.controllerMesh) {
            this.controllerMesh.destroy();
            this.controllerMesh = undefined;
        }
        this.controllerInfo = undefined;
        this.handInfo = undefined;
        this.facet = undefined;
        for (const uid of this.attachedRenderables.keys()) {
            this.owner.renderer.removeExistingModel(uid, Date.now());
        }
        this.attachedRenderables.clear();
    }
    setVisible(visible) {
        this.handMesh.setVisible(visible);
        if (this.controllerMesh) {
            this.controllerMesh.setVisible(visible);
        }
    }
    updateFacet(facet) {
        var _a, _b, _c, _d;
        if (facet.controllerType === InputDeviceType.Mouse) {
            this.handMesh.destroy();
            if (this.controllerMesh !== undefined) {
                this.controllerMesh.destroy();
                this.controllerMesh = undefined;
            }
            this.controllerInfo = undefined;
            this.handInfo = undefined;
            this.facet = facet;
            return;
        }
        // Hand mesh (Hand_A_17) is shown whenever using VR controllers and NOT using hand-tracking.
        // isShowingVRController only controls the additional controller shape mesh.
        const showHandMesh = !facet.hasTrackedHand;
        const showControllerMesh = facet.isShowingVRController && !facet.hasTrackedHand;
        const prevShowHandMesh = this.facet ? !this.facet.hasTrackedHand : false;
        const prevShowControllerMesh = ((_b = (_a = this.facet) === null || _a === void 0 ? void 0 : _a.isShowingVRController) !== null && _b !== void 0 ? _b : false) && !((_d = (_c = this.facet) === null || _c === void 0 ? void 0 : _c.hasTrackedHand) !== null && _d !== void 0 ? _d : true);
        const handMeshBecameVisible = showHandMesh && !prevShowHandMesh;
        const controllerMeshBecameVisible = showControllerMesh && !prevShowControllerMesh;
        const needsHandNewMesh = !this.facet || this.facet.hand !== facet.hand;
        const needsControllerNewMesh = !this.facet || this.facet.controllerType !== facet.controllerType;
        const handColorChanged = this.facet !== undefined && this.facet.baseColor !== facet.baseColor;
        if (showHandMesh && (needsHandNewMesh || handMeshBecameVisible)) {
            this.handInfo = this.getHandMeshInfo(facet.hand);
            this.loadAndShowHandMesh();
        }
        else if (!showHandMesh && (prevShowHandMesh || this.handMesh.isLoaded)) {
            this.handMesh.destroy();
            this.handInfo = undefined;
        }
        if (showControllerMesh) {
            if (needsControllerNewMesh || controllerMeshBecameVisible) {
                this.controllerInfo = getControllerMeshInfo(facet.controllerType, facet.hand);
                this.loadControllerMesh();
            }
        }
        else if (this.controllerMesh) {
            this.controllerMesh.destroy();
            this.controllerMesh = undefined;
        }
        if (showHandMesh && handColorChanged) {
            this.applyHandColor();
        }
        this.handMesh.setVisible(showHandMesh);
    }
    /**
     * Shows (or hides) the pressed/unpressed version of the button.
     * @param facet
     */
    onButtonUpdate(facet) {
        if (this.controllerInfo && this.facet && this.facet.isShowingVRController) {
            this.updateControllerButtonSubmeshVisibilities(facet, this.controllerInfo);
        }
        if (this.handInfo) {
            this.updateHandSubmeshVisibilities(facet, this.handInfo);
        }
    }
    updateTransform() {
        tempTransform.copy(this.owner.matrixWorld);
        this.handMesh.updateTransform(tempTransform);
        if (this.controllerMesh !== undefined) {
            this.controllerMesh.updateTransform(this.owner.matrixWorld);
        }
        for (const data of this.attachedRenderables.values()) {
            tempTransform.multiplyMatrices(this.owner.matrixWorld, data.offsetMatrix);
            data.model.updateTransform(tempTransform);
            const model = modelPool.getModel(data.model.modelUID);
            if (model === undefined)
                continue;
            const localTransform = computeLocalMatrixFromWorldMatrix(model, tempTransform);
            model.setTransform(localTransform, useExternalMessages(this.owner.renderer.pendingMessages));
        }
    }
    getHandMeshInfo(hand) {
        if (hand === ControllerHand.RightHand) {
            return RIGHT_HAND_MESH_INFO;
        }
        else if (hand === ControllerHand.LeftHand) {
            return LEFT_HAND_MESH_INFO;
        }
        else {
            console.warn("ControllerHand is unknown. Setting the Hand to right-hand-side. Please implement a better solution");
            return RIGHT_HAND_MESH_INFO;
        }
    }
    loadAndShowHandMesh() {
        if (this.handInfo === undefined)
            throw new Error("Do call this function only after assigning this.handInfo");
        this.handMesh.destroy();
        const subMeshVisibilities = {};
        for (const buttonGesture of this.handInfo.meshesToBeUsedOnButtonPress) {
            if (buttonGesture.subMeshNames)
                subMeshVisibilities[buttonGesture.subMeshNames.pressed] = false;
        }
        this.handMesh.setCastShadow(false);
        this.handMesh.load(this.handInfo.file, true);
        this.handMesh.updateSubMeshVisibilities(subMeshVisibilities);
    }
    loadControllerMesh() {
        if (this.controllerInfo === undefined) {
            throw new Error("Do call this function only after assigning this.controllerInfo");
        }
        if (this.controllerMesh) {
            this.controllerMesh.destroy();
        }
        else {
            this.controllerMesh = new MeshInstance(this.owner.renderer, this.onControllerMeshLoaded);
        }
        const subMeshVisibilities = {};
        for (const button of this.controllerInfo.buttons) {
            if (button.subMeshNames)
                subMeshVisibilities[button.subMeshNames.pressed] = false;
        }
        this.controllerMesh.setCastShadow(false);
        this.controllerMesh.load(this.controllerInfo.file);
        this.controllerMesh.updateSubMeshVisibilities(subMeshVisibilities);
    }
    updateControllerButtonSubmeshVisibilities(facet, controllerInfo) {
        if (this.controllerMesh === undefined)
            return;
        for (const button of controllerInfo.buttons) {
            if (button.index === facet.buttonIndex) {
                if (button.subMeshNames) {
                    this.controllerMesh.setSubMeshVisibility(button.subMeshNames.pressed, facet.pressed);
                    this.controllerMesh.setSubMeshVisibility(button.subMeshNames.released, !facet.pressed);
                }
            }
        }
    }
    updateHandSubmeshVisibilities(facet, handInfo) {
        for (const button of handInfo.meshesToBeUsedOnButtonPress) {
            if (button.index === facet.buttonIndex) {
                if (button.subMeshNames) {
                    this.handMesh.setSubMeshVisibility(button.subMeshNames.pressed, facet.pressed);
                    this.handMesh.setSubMeshVisibility(button.subMeshNames.released, !facet.pressed);
                }
            }
        }
    }
    applyHandColor() {
        const facet = this.facet;
        if (facet === undefined)
            return;
        this.handMesh.traverseMaterial(ShaderMaterial, (mat) => {
            mat.uniforms.strokeColor.value.setHex(0xffffff);
            mat.uniforms.baseColor.value.setHex(facet.baseColor);
        });
    }
    sanityCheckHand() {
        if (this.handInfo === undefined) {
            console.error("Sanity-Check: this.handInfo is undefined!");
            return;
        }
        const handInfo = this.handInfo;
        for (const gesture of handInfo.meshesToBeUsedOnButtonPress) {
            const subMeshNames = gesture.subMeshNames;
            if (subMeshNames === undefined) {
                continue; // do we want to throw a warning here, too?
            }
            if (!this.handMesh.hasSubmesh(subMeshNames.pressed)) {
                console.error("Sanity-Check: Hand " +
                    handInfo.type +
                    " is missing the submesh '" +
                    subMeshNames.pressed +
                    "'. \n (Hint: when preparing the exports in blender, make sure the mesh-component of the sub-mesh-object has that same name, too!)");
            }
            if (!this.handMesh.hasSubmesh(subMeshNames.released)) {
                console.error("Sanity-Check: Hand " +
                    handInfo.type +
                    " is missing the submesh '" +
                    subMeshNames.released +
                    "'. \n (Hint: when preparing the exports in blender, make sure the mesh-component of the sub-mesh-object has that same name, too!)");
            }
        }
    }
    sanityCheckController(verbose = true) {
        if (this.controllerInfo === undefined) {
            console.error("Sanity-Check: this.controllerInfo is undefined!");
            return;
        }
        if (this.controllerMesh === undefined) {
            console.error("Sanity-Check: this.controllerMeshRenderable is undefined!");
            return;
        }
        const controllerInfo = this.controllerInfo;
        let isMissingSubmeshes = false;
        for (const button of controllerInfo.buttons) {
            const subMeshNames = button.subMeshNames;
            if (subMeshNames === undefined) {
                continue; // do we want to throw a warning here, too?
            }
            if (!this.controllerMesh.hasSubmesh(subMeshNames.pressed)) {
                isMissingSubmeshes = true;
                if (verbose)
                    console.error("Sanity-Check: Controller-Mesh " +
                        controllerInfo.name +
                        " is missing the submesh '" +
                        subMeshNames.pressed +
                        "'. \n (Hint: when preparing the exports in blender, make sure the mesh-component of the sub-mesh-object has that same name, too!)");
            }
            if (!this.controllerMesh.hasSubmesh(subMeshNames.released)) {
                isMissingSubmeshes = true;
                if (verbose)
                    console.error("Sanity-Check: Controller-Mesh " +
                        controllerInfo.name +
                        " is missing the submesh '" +
                        subMeshNames.released +
                        "'. \n (Hint: when preparing the exports in blender, make sure the mesh-component of the sub-mesh-object has that same name, too!)");
            }
        }
        if (isMissingSubmeshes) {
            console.warn("Sanity-Check: Controller-Mesh " + controllerInfo.name + " is missing is missing one or more submeshes.\nSome button-presses might not be displayed.");
        }
    }
}
ControllerRenderable.typestatic = "Controller";
